export type EmptyListSurface =
  | "activeLoads"
  | "loadBoard"
  | "marginRows"
  | "exceptions"
  | "approvals"
  | "timeline"
  | "savedViews"
  | "pinnedLoads"
  | "filteredLoads"
  | "customerReferences"
  | "charges"
  | "capacityMatches"
  | "lanePreferences";

export interface EmptyListState {
  title: string;
  detail: string;
  actionLabel: string;
  actionHref: `#${string}` | `/${string}`;
}

export const EMPTY_LIST_STATES: Record<EmptyListSurface, EmptyListState> = {
  activeLoads: {
    title: "No active loads",
    detail: "Create or import a load before the dispatcher workspace can rank work.",
    actionLabel: "Start a load draft",
    actionHref: "#command",
  },
  loadBoard: {
    title: "No load-board activity",
    detail: "Post a held load-board draft or run capacity matching when a load is ready.",
    actionLabel: "Review load drafts",
    actionHref: "#command",
  },
  marginRows: {
    title: "No margin history",
    detail: "Booked charges will appear here after loads have customer and carrier amounts.",
    actionLabel: "Review charges",
    actionHref: "#margin",
  },
  exceptions: {
    title: "No open exceptions",
    detail: "Tracking, compliance, detention, and document issues will appear here.",
    actionLabel: "Review timeline",
    actionHref: "#timeline",
  },
  approvals: {
    title: "No pending approvals",
    detail: "Draft-and-hold actions appear here before money, customer, or commitment work moves forward.",
    actionLabel: "Open reviewer inbox",
    actionHref: "/inbox",
  },
  timeline: {
    title: "No timeline events",
    detail: "Agent actions, evidence, audit events, and reviewer decisions will appear here.",
    actionLabel: "Review active loads",
    actionHref: "#command",
  },
  savedViews: {
    title: "No saved views",
    detail: "Save the current filters to reuse a dispatch queue.",
    actionLabel: "Save current view",
    actionHref: "#saved-views",
  },
  pinnedLoads: {
    title: "No pinned loads",
    detail: "Pin active loads that need repeated dispatcher attention.",
    actionLabel: "Review visible loads",
    actionHref: "#saved-views",
  },
  filteredLoads: {
    title: "No loads match these filters",
    detail: "Clear or adjust filters to bring active loads back into the queue.",
    actionLabel: "Reset filters",
    actionHref: "#saved-views",
  },
  customerReferences: {
    title: "No customer references",
    detail: "Pickup, BOL, PRO, and customer reference values will appear after generation or entry.",
    actionLabel: "Review load actions",
    actionHref: "#timeline",
  },
  charges: {
    title: "No charges yet",
    detail: "Linehaul, fuel, accessorial, and carrier-pay rows will appear after rating or dispatch.",
    actionLabel: "Review load actions",
    actionHref: "#timeline",
  },
  capacityMatches: {
    title: "No carrier matches yet",
    detail: "Run capacity matching to rank lane preferences and held carrier outreach drafts.",
    actionLabel: "Run match",
    actionHref: "#capacity-match",
  },
  lanePreferences: {
    title: "No lane preferences",
    detail: "Save preferred carrier lanes to improve future capacity matching.",
    actionLabel: "Save lane preference",
    actionHref: "#lane-preferences",
  },
};

export function getEmptyListState(surface: EmptyListSurface): EmptyListState {
  return EMPTY_LIST_STATES[surface];
}
